import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    FormControl,
    FormHelperText, // Add this
    InputLabel,
    Select,
    MenuItem,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    CircularProgress,
    Alert,
    Divider,
} from "@mui/material";
import axios from "axios";
import AxiosWrapper from "../../../basecomp/base/AxiosWrapper";
import EditableOHQ from "./EditableOHQ"
import CBackDrop from "../../../basecomp/components/CBackDrop";

const { axiosPostCallWithToken, axiosGetCallWithToken } = AxiosWrapper;

const Base_URL = process.env.REACT_APP_BE_URL;

const mainCauses = [
    "Inventory Mismatch",
    "Incorrect Bin Location",
    "Data Entry Error",
    "Material Damage",
    "Missing Material",
    "System Error",
];

const subCauses = [
    "Incorrect physical count",
    "Material stored elsewhere",
    "Wrong material identification",
    "Incorrect quantity entry",
    "Damaged material",
    "Missing stock",
    "Duplicate entry",
    "Incorrect unit of measurement",
    "Stock transfer pending",
    "Other",
];

const causeMapping = {
    "Inbound": [
        "Not physically received, still in SAP received",
        "Physically received, in SAP still open",
        "Cancelled PO",
        "Taken by maintenance before receipt",
        "Put away to wrong location",
        "Wrong identification at receipt",
        "Rework procedure not followed",
        "Rework supplier procedure not followed",
    ],

    "Outbound to Work Order": [
        "Wrong QTY picked",
        "Wrong UOM picked",
        "SAP not updated timely",
        "Wrong part picked",
        "Not picked, still in SAP deducted",
        "Picked, but not in SAP deducted",
        "Canceled work order",
        "Identified with wrong part number",
    ],

    "Corrections": [
        "Part number changed",
        "Wrong earlier cycle count",
        "Wrong correction in the past",
        "Physically scrapped, but not in SAP",
        "Scrapped in SAP, but Physically remain",
    ],

    "Location Management": [
        "Location not orderly fitted out",
        "No clear location identification",
        "Part in wrong location",
        "Location too small",
        "No clear partnumber",
    ],

    "Reason unknown": [
        "Problem investigated, but no cause found",
    ],
};

const RcaModal = ({ open, onClose, material, onSubmitSuccess, viewOnly }) => {
    const [mainCause, setMainCause] = useState("");
    const [subCause, setSubCause] = useState("");
    const [description, setDescription] = useState("");
    const [resolutionStatus, setResolutionStatus] = useState("resolved");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [ohq, setOhq] = useState(null)



    useEffect(() => {
        if (!open || !material?.material_counting_id) return;

        const fetchRcaRecord = async () => {
            setLoading(true);
            setError("");

            try {
        
                const response = await axiosGetCallWithToken(
                    `${Base_URL}/label_print/rca-records/${material.material_counting_id}`
                );

                 

                // Adjust this path to match your API response structure.
                const record = response?.data?.data;
                console.log("Fetched RCA record:", record);
                if (record) {
                    setMainCause(record.cause ?? "");
                    setSubCause(record.sub_cause ?? "");
                    setDescription(record.description ?? "");
                    setResolutionStatus(record.status ?? "resolved");
                } else {
                    resetForm();
                }

         
            } catch (err) {
                console.error("Failed to fetch RCA record:", err);
                setError(
                    err.response?.data?.message ||
                    "Failed to fetch existing RCA record."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchRcaRecord();
    }, [open, material?.material_counting_id]);

    const resetForm = () => {
        setMainCause("");
        setSubCause("");
        setDescription("");
        setResolutionStatus("resolved");
        setErrors({});
        setTouched({});
    };

    const validateForm = () => {
        const newErrors = {};

        if (!mainCause) {
            newErrors.mainCause = "Please select a main cause";
        }

        if (!subCause) {
            newErrors.subCause = "Please select a sub cause";
        }


        if (!resolutionStatus) {
            newErrors.resolutionStatus = "Please select a resolution status";
        }

        setErrors(newErrors);
        setTouched({
            mainCause: true,
            subCause: true,
            description: true,
            resolutionStatus: true,
        });

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError("");

        const payload = {
            material_id: material?.id,
            material_no: material?.material_no,
            material_counting_id: material?.material_counting_id,
            main_cause: mainCause,
            sub_cause: subCause,
            description: description.trim(),
            resolution_status: resolutionStatus,
            new_ohq: ohq,
        };


        try {
            await axiosPostCallWithToken(`${Base_URL}/label_print/rca-submit`, payload);

            onSubmitSuccess?.(ohq);
            handleClose();
        } catch (err) {
            console.error("Failed to submit RCA:", err);
            setError(
                err.response?.data?.message || "Failed to submit. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        setError("");
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Root Cause Analysis</DialogTitle>
            <CBackDrop cOpen={loading} />

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2.5}>
                    {/* Material Details */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                            Material Details
                        </Typography>

                        <Box
                            sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "action.hover",
                            }}
                        >
                            <Detail label="Material Number" value={material?.material_no} />
                            <Detail label="Description" value={material?.material_desc} />
                            <Detail label="Bin Location" value={material?.bin_location} />
                            <Detail label="Bulk Location" value={material?.bulk_location} />
                            <Detail label="Difference" value={material?.summary} />
                            <Detail label="OHQ" value={material?.ohqty} />

                            <EditableOHQ
                                value={material?.physical_qty}
                                onChange={(newOhq) => {
                                    console.log("Selected OHQ:", newOhq);
                                    setOhq(newOhq)

                                    // Call your API here
                                }}
                            />

                        </Box>
                    </Box>

                    <Divider />

                    {/* Main Cause */}
                    <FormControl fullWidth required error={Boolean(touched.mainCause && errors.mainCause)}>
                        <InputLabel id="main-cause-label">Main Cause</InputLabel>

                        <Select
                            labelId="main-cause-label"
                            value={mainCause}
                            label="Main Cause"
                            onChange={(e) => {
                                setMainCause(e.target.value);
                                setSubCause("");

                                setErrors((prev) => ({
                                    ...prev,
                                    mainCause: "",
                                    subCause: "",
                                }));
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, mainCause: true }))}
                        >
                            {Object.keys(causeMapping).map((cause) => (
                                <MenuItem key={cause} value={cause}>
                                    {cause}
                                </MenuItem>
                            ))}
                        </Select>

                        {touched.mainCause && errors.mainCause && (
                            <FormHelperText>{errors.mainCause}</FormHelperText>
                        )}
                    </FormControl>

                    {/* Sub Cause */}
                    <FormControl fullWidth required error={Boolean(touched.subCause && errors.subCause)}>
                        <InputLabel id="sub-cause-label">Sub Cause</InputLabel>

                        <Select
                            labelId="sub-cause-label"
                            value={subCause}
                            label="Sub Cause"
                            disabled={!mainCause}
                            onChange={(e) => {
                                setSubCause(e.target.value);
                                setErrors((prev) => ({
                                    ...prev,
                                    subCause: "",
                                }));
                            }}
                            onBlur={() =>
                                setTouched((prev) => ({
                                    ...prev,
                                    subCause: true,
                                }))
                            }
                        >
                            {(causeMapping[mainCause] || []).map((cause) => (
                                <MenuItem key={cause} value={cause}>
                                    {cause}
                                </MenuItem>
                            ))}
                        </Select>

                        {touched.subCause && errors.subCause && (
                            <FormHelperText>{errors.subCause}</FormHelperText>
                        )}
                    </FormControl>

                    {/* Description */}
                    <TextField
                        label="Comments / Description"
                        placeholder="Describe the root cause and any corrective action..."
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setErrors((prev) => ({ ...prev, description: "" }));
                        }}
                        onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                        multiline
                        rows={4}
                        fullWidth
                        
                        error={Boolean(touched.description && errors.description)}
                        helperText={touched.description ? errors.description : ""}
                    />

                    {/* Resolution Status */}
                    <FormControl error={Boolean(touched.resolutionStatus && errors.resolutionStatus)}>
                        <Typography variant="subtitle2" mb={0.5}>
                            Resolution Status *
                        </Typography>

                        <RadioGroup
                            row
                            value={resolutionStatus}
                            onChange={(e) => {
                                setResolutionStatus(e.target.value);
                                setErrors((prev) => ({ ...prev, resolutionStatus: "" }));
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, resolutionStatus: true }))}
                        >
                            <FormControlLabel
                                value="resolved"
                                control={<Radio />}
                                label="Resolved"
                            />
                            <FormControlLabel
                                value="not_resolved"
                                control={<Radio />}
                                label="Not Resolved"
                            />
                        </RadioGroup>

                        {touched.resolutionStatus && errors.resolutionStatus && (
                            <FormHelperText>{errors.resolutionStatus}</FormHelperText>
                        )}
                    </FormControl>

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} disabled={loading} color="inherit">
                    Cancel
                </Button>

                {!viewOnly &&
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            "Submit"
                        )}
                    </Button>}
            </DialogActions>
        </Dialog>
    );
};

const Detail = ({ label, value }) => (
    <Box
        display="flex"
        justifyContent="space-between"
        gap={2}
        py={0.5}
    >
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={500}
            textAlign="right"
            sx={{ overflowWrap: "anywhere" }}
        >
            {value || "-"}
        </Typography>
    </Box>
);

export default RcaModal;